import { Accordion } from '@delmonicos/shared-design-system-react/src/components/accordion/accordion';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { CopyToClipboard } from '@delmonicos/shared-design-system-react/src/components/copy-to-clipboard/copy-to-clipboard';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import {
  Icon,
  IconSize,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import {
  Text,
  TextColor,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { useTranslation } from 'react-i18next';

import { DAYS } from '@app/feature/location/days';
import groupedFacilities from '@app/feature/location/facilities';

import { useLocationContext } from '../../../location.context';
import * as css from './information-card.module.scss';

export const InformationCardComponent = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const location = useLocationContext();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card direction="column" gap={GapEnum.M}>
      <Stack gap={GapEnum.M} justifyContent="space-between" alignItems="center">
        <AccentTitle
          as={HeadingEnum.H2}
          icon="info-circle"
          text={t('$.location-details.information.title')}
        />

        <Stack
          gap={GapEnum.M}
          justifyContent="space-between"
          alignItems="center">
          <Text fontSize={TextFontSize.Small} color={TextColor.Disabled}>
            {t('delmonicos-shared-sdk.outputs-base.id')}
          </Text>
          <Text fontSize={TextFontSize.Small} color={TextColor.Disabled}>
            {location.id}
          </Text>
        </Stack>
      </Stack>

      <Stack gap={GapEnum.M} direction="column">
        <Stack gap={GapEnum.XS} alignItems="center">
          <Icon name="info-circle" iconSize={IconSize.Small} />
          <Text bold fontSize={TextFontSize.Medium}>
            {t('delmonicos-shared-sdk.outputs-location.ocpi-location-id')}
          </Text>
        </Stack>
        <CopyToClipboard textToCopy={location.ocpiLocationId}>
          <Text>{location.ocpiLocationId}</Text>
        </CopyToClipboard>
      </Stack>

      <Stack
        gap={GapEnum.M}
        justifyContent="space-between"
        alignItems="center"
        className={css.flexedItemsContainer}>
        <Stack gap={GapEnum.XS} alignItems="center">
          <Icon name="parking" iconSize={IconSize.Small} />
          <Text
            fontSize={TextFontSize.Medium}
            color={TextColor.Secondary}
            uppercase>
            {t('delmonicos-shared-sdk.outputs-location.type')}
          </Text>
        </Stack>

        <Tag className="bg-surface-brand-decorative">
          <Text fontSize={TextFontSize.Small} italic>
            {t(
              `delmonicos-shared-sdk.ocpi-protocol-enums.location-type.${location.type}`,
            )}
          </Text>
        </Tag>
      </Stack>

      <Accordion
        title={
          <Stack gap={GapEnum.XS} alignItems="center">
            <Icon name="clock" iconSize={IconSize.Small} />
            <Text bold>
              {t('delmonicos-shared-sdk.outputs-location.opening-times')}
            </Text>
          </Stack>
        }>
        <Stack direction="column" gap={GapEnum.S}>
          {location?.openingTimes?.twentyfourseven && (
            <Text>
              {t('delmonicos-shared-sdk.outputs-hours.twentyfourseven')}
            </Text>
          )}
          {!location?.openingTimes?.twentyfourseven &&
            DAYS.map((day, index) => (
              <Stack justifyContent="space-between" key={day} gap={GapEnum.S}>
                <Text>
                  {t(
                    `delmonicos-shared-sdk.ocpi-protocol-enums.day-of-week.${day}`,
                  )}
                </Text>
                <Stack justifyContent="space-between" gap={GapEnum.L}>
                  <Text bold>
                    {
                      location?.openingTimes?.regularHours?.find(
                        (h) => h.weekday === index + 1,
                      )?.periodBegin
                    }
                  </Text>
                  <Text bold>-</Text>
                  <Text bold>
                    {
                      location?.openingTimes?.regularHours?.find(
                        (h) => h.weekday === index + 1,
                      )?.periodEnd
                    }
                  </Text>
                </Stack>
              </Stack>
            ))}
        </Stack>
      </Accordion>

      <Stack gap={GapEnum.XS} alignItems="center">
        <Icon name="parking" iconSize={IconSize.Small} />
        <Text fontSize={TextFontSize.Medium} bold>
          {t('delmonicos-shared-sdk.outputs-location.facilities')}
        </Text>
      </Stack>

      <Stack gap={GapEnum.S} direction="column">
        {groupedFacilities.map(({ title, facilities }) => {
          const selectedFacilities = facilities.filter((facility) =>
            location.facilities?.includes(facility.name),
          );

          if (selectedFacilities.length === 0) return null;

          return (
            <Stack gap={GapEnum.XS} key={title} direction="column">
              <Text fontSize={TextFontSize.XSmall} uppercase>
                {t(title)}
              </Text>

              <Stack gap={GapEnum.XS}>
                {selectedFacilities.map((facility) => (
                  <Tag
                    key={facility.name}
                    gap={GapEnum.XS}
                    className="bg-surface-brand-decorative">
                    <Icon name={facility.icon} iconSize={IconSize.Small} />
                    <Text fontSize={TextFontSize.Small} italic>
                      {t(
                        `delmonicos-shared-sdk.ocpi-protocol-enums.facility.${facility.name}`,
                      )}
                    </Text>
                  </Tag>
                ))}
              </Stack>
            </Stack>
          );
        })}
      </Stack>

      <Divider />

      <Stack gap={GapEnum.M} direction="column">
        <Stack gap={GapEnum.XS} alignItems="center">
          <Icon name="location" iconSize={IconSize.Small} />
          <Text bold>
            {t('delmonicos-shared-sdk.outputs-location.directions')}
          </Text>
        </Stack>
        {location.directions?.map(({ text, language }) => (
          <Text key={text}>
            <Text className={css.language}>
              {t(`delmonicos-shared-sdk.enums-language.${language}`)}
            </Text>{' '}
            - {text}
          </Text>
        ))}
      </Stack>
    </Card>
  );
};
